import { useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import { Item, Tag } from '@/types/items';

/**
 * Character for equipping items.
 */
interface CharacterOption {
    id: number;
    name: string;
    class: string;
}

interface Props {
    items: Item[];
    characters: CharacterOption[];
    stats: Record<string, string>;
    allTags: Tag[];
    selectedTags: string[];
}

/**
 * User Items Index page - displays all items owned by the user.
 * Supports filtering by tags and managing tags on items.
 */
export default function ItemsIndex({ items, characters, stats, allTags, selectedTags }: Props) {
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const [showEquipModal, setShowEquipModal] = useState(false);
    const [showTagModal, setShowTagModal] = useState(false);
    const [tagModalItem, setTagModalItem] = useState<Item | null>(null);

    // Handle equip button click
    const handleEquipClick = (item: Item) => {
        setSelectedItem(item);
        setShowEquipModal(true);
    };

    // Handle equip to character
    const handleEquip = (characterId: number) => {
        if (!selectedItem) return;
        router.patch(`/items/${selectedItem.id}/equip`, {
            character_id: characterId,
        }, {
            preserveScroll: true,
            onSuccess: () => {
                setShowEquipModal(false);
                setSelectedItem(null);
            },
        });
    };

    // Handle unequip
    const handleUnequip = (item: Item) => {
        router.patch(`/items/${item.id}/unequip`, {}, {
            preserveScroll: true,
        });
    };

    // Handle tag filter toggle
    const handleTagFilter = (tagName: string) => {
        const currentTags = [...selectedTags];
        const index = currentTags.findIndex(t => t.toLowerCase() === tagName.toLowerCase());

        if (index >= 0) {
            // Remove tag from filter
            currentTags.splice(index, 1);
        } else {
            // Add tag to filter
            currentTags.push(tagName);
        }

        // Navigate with updated tag filter
        const params = currentTags.length > 0 ? { tags: currentTags.join(',') } : {};
        router.get('/items', params, { preserveState: true, preserveScroll: true });
    };

    // Clear all tag filters
    const clearTagFilters = () => {
        router.get('/items', {}, { preserveState: true, preserveScroll: true });
    };

    // Open tag management modal for an item
    const handleManageTags = (item: Item) => {
        setTagModalItem(item);
        setShowTagModal(true);
    };

    // Group items by equipped status
    const equippedItems = items.filter(item => item.is_equipped);
    const unequippedItems = items.filter(item => !item.is_equipped);

    return (
        <>
            <Head title="My Items" />

            <div className="min-h-screen bg-slate-900 py-8 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="flex items-center justify-between mb-6">
                        <h1 className="text-2xl font-bold text-white">My Items</h1>
                        <Link
                            href="/map"
                            className="text-gray-400 hover:text-white transition-colors"
                        >
                            ← Back to Map
                        </Link>
                    </div>

                    {/* Tag Filter Section */}
                    {allTags.length > 0 && (
                        <div className="mb-6 p-4 bg-slate-800 border border-slate-700 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-300">Filter by Tags</h3>
                                {selectedTags.length > 0 && (
                                    <button
                                        onClick={clearTagFilters}
                                        className="text-xs text-gray-400 hover:text-white transition-colors"
                                    >
                                        Clear filters
                                    </button>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {allTags.map(tag => {
                                    const isSelected = selectedTags.some(
                                        t => t.toLowerCase() === tag.name.toLowerCase()
                                    );
                                    return (
                                        <button
                                            key={tag.id}
                                            onClick={() => handleTagFilter(tag.name)}
                                            className={`px-3 py-1 text-sm rounded-full transition-colors ${
                                                isSelected
                                                    ? 'bg-purple-600 text-white'
                                                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                                            }`}
                                        >
                                            {tag.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {items.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <p className="text-lg">
                                {selectedTags.length > 0
                                    ? 'No items match the selected tags.'
                                    : "You don't have any items yet."}
                            </p>
                            <p className="text-sm mt-2">
                                {selectedTags.length > 0
                                    ? 'Try removing some tag filters.'
                                    : 'Items are awarded by Game Masters for your adventures!'}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {/* Equipped Items */}
                            {equippedItems.length > 0 && (
                                <div>
                                    <h2 className="text-lg font-semibold text-green-400 mb-4">
                                        Equipped Items ({equippedItems.length})
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {equippedItems.map(item => (
                                            <ItemCard
                                                key={item.id}
                                                item={item}
                                                stats={stats}
                                                onEquip={() => handleEquipClick(item)}
                                                onUnequip={() => handleUnequip(item)}
                                                onManageTags={() => handleManageTags(item)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Unequipped Items */}
                            {unequippedItems.length > 0 && (
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-400 mb-4">
                                        Inventory ({unequippedItems.length})
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {unequippedItems.map(item => (
                                            <ItemCard
                                                key={item.id}
                                                item={item}
                                                stats={stats}
                                                onEquip={() => handleEquipClick(item)}
                                                onUnequip={() => handleUnequip(item)}
                                                onManageTags={() => handleManageTags(item)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Equip Modal */}
                {showEquipModal && selectedItem && (
                    <EquipModal
                        item={selectedItem}
                        characters={characters}
                        onEquip={handleEquip}
                        onClose={() => {
                            setShowEquipModal(false);
                            setSelectedItem(null);
                        }}
                    />
                )}

                {/* Tag Management Modal */}
                {showTagModal && tagModalItem && (
                    <TagModal
                        item={tagModalItem}
                        allTags={allTags}
                        onClose={() => {
                            setShowTagModal(false);
                            setTagModalItem(null);
                        }}
                    />
                )}
            </div>
        </>
    );
}

/**
 * ItemCard - Displays a single item with its stats, tags, and actions.
 */
function ItemCard({
    item,
    stats,
    onEquip,
    onUnequip,
    onManageTags,
}: {
    item: Item;
    stats: Record<string, string>;
    onEquip: () => void;
    onUnequip: () => void;
    onManageTags: () => void;
}) {
    return (
        <div className={`bg-slate-800 border rounded-lg p-4 ${
            item.is_equipped ? 'border-green-600' : 'border-slate-700'
        }`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div>
                    <h3 className={`font-medium ${item.rarity.tailwindClass}`}>
                        {item.name}
                    </h3>
                    <p className="text-xs text-gray-500">{item.rarity.label}</p>
                </div>
                <span className="text-yellow-400 text-sm">{item.quality_stars}</span>
            </div>

            {/* Description */}
            {item.description && (
                <p className="text-sm text-gray-400 mb-3 line-clamp-2">{item.description}</p>
            )}

            {/* Tags */}
            {item.tags && item.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                    {item.tags.map(tag => (
                        <span
                            key={tag.id}
                            className="px-2 py-0.5 text-xs bg-purple-900/50 text-purple-300 rounded-full"
                        >
                            {tag.name}
                        </span>
                    ))}
                </div>
            )}

            {/* Stats */}
            <div className="space-y-1 mb-4">
                {Object.entries(item.stats).map(([key, stat]) => (
                    <div key={key} className="flex justify-between text-sm">
                        <span className="text-gray-400">{stat.label}</span>
                        <span className="text-green-400">+{stat.value}</span>
                    </div>
                ))}
                <div className="flex justify-between text-sm pt-1 border-t border-slate-700">
                    <span className="text-gray-500">Total Bonus</span>
                    <span className="text-white font-medium">+{item.total_stat_bonus}</span>
                </div>
            </div>

            {/* Equipped Status */}
            {item.is_equipped && item.character_name && (
                <p className="text-xs text-green-400 mb-3">
                    Equipped on {item.character_name}
                </p>
            )}

            {/* Actions */}
            <div className="flex gap-2">
                {item.is_equipped ? (
                    <button
                        onClick={onUnequip}
                        className="flex-1 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                        Unequip
                    </button>
                ) : (
                    <button
                        onClick={onEquip}
                        className="flex-1 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                    >
                        Equip
                    </button>
                )}
                <button
                    onClick={onManageTags}
                    className="px-3 py-1.5 text-sm bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                    title="Manage tags"
                >
                    🏷️
                </button>
            </div>
        </div>
    );
}

/**
 * EquipModal - Modal for selecting which character to equip an item to.
 */
function EquipModal({
    item,
    characters,
    onEquip,
    onClose,
}: {
    item: Item;
    characters: CharacterOption[];
    onEquip: (characterId: number) => void;
    onClose: () => void;
}) {
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full mx-4">
                <h2 className="text-lg font-semibold text-white mb-4">
                    Equip {item.name}
                </h2>

                {characters.length === 0 ? (
                    <p className="text-gray-400 mb-4">
                        You don't have any characters to equip this item to.
                    </p>
                ) : (
                    <div className="space-y-2 mb-4">
                        <p className="text-sm text-gray-400 mb-3">Select a character:</p>
                        {characters.map(char => (
                            <button
                                key={char.id}
                                onClick={() => onEquip(char.id)}
                                className="w-full px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded text-left transition-colors"
                            >
                                <span className="text-white font-medium">{char.name}</span>
                                <span className="text-gray-400 text-sm ml-2">({char.class})</span>
                            </button>
                        ))}
                    </div>
                )}

                <button
                    onClick={onClose}
                    className="w-full px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

/**
 * TagModal - Modal for managing tags on an item.
 * Allows adding existing tags or creating new ones.
 */
function TagModal({
    item,
    allTags,
    onClose,
}: {
    item: Item;
    allTags: Tag[];
    onClose: () => void;
}) {
    const [newTagName, setNewTagName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Get IDs of tags already on this item
    const itemTagIds = new Set(item.tags.map(t => t.id));

    // Tags available to add (not already on item)
    const availableTags = allTags.filter(t => !itemTagIds.has(t.id));

    // Handle adding a tag to the item
    const handleAddTag = (tagName: string) => {
        setIsSubmitting(true);
        router.post(`/items/${item.id}/tags`, { tag: tagName }, {
            preserveScroll: true,
            onSuccess: () => {
                setNewTagName('');
                setIsSubmitting(false);
            },
            onError: () => setIsSubmitting(false),
        });
    };

    // Handle removing a tag from the item
    const handleRemoveTag = (tagId: number) => {
        router.delete(`/items/${item.id}/tags/${tagId}`, {
            preserveScroll: true,
        });
    };

    // Handle creating and adding a new tag
    const handleCreateTag = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTagName.trim()) return;
        handleAddTag(newTagName.trim());
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
                <h2 className="text-lg font-semibold text-white mb-4">
                    Manage Tags: {item.name}
                </h2>

                {/* Current Tags */}
                <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-300 mb-2">Current Tags</h3>
                    {item.tags.length === 0 ? (
                        <p className="text-sm text-gray-500">No tags assigned</p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {item.tags.map(tag => (
                                <span
                                    key={tag.id}
                                    className="inline-flex items-center gap-1 px-3 py-1 bg-purple-900/50 text-purple-300 rounded-full text-sm"
                                >
                                    {tag.name}
                                    <button
                                        onClick={() => handleRemoveTag(tag.id)}
                                        className="ml-1 text-purple-400 hover:text-red-400 transition-colors"
                                        title="Remove tag"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Add Existing Tag */}
                {availableTags.length > 0 && (
                    <div className="mb-4">
                        <h3 className="text-sm font-medium text-gray-300 mb-2">Add Existing Tag</h3>
                        <div className="flex flex-wrap gap-2">
                            {availableTags.map(tag => (
                                <button
                                    key={tag.id}
                                    onClick={() => handleAddTag(tag.name)}
                                    disabled={isSubmitting}
                                    className="px-3 py-1 text-sm bg-slate-700 text-gray-300 rounded-full hover:bg-slate-600 transition-colors disabled:opacity-50"
                                >
                                    + {tag.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Create New Tag */}
                <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-300 mb-2">Create New Tag</h3>
                    <form onSubmit={handleCreateTag} className="flex gap-2">
                        <input
                            type="text"
                            value={newTagName}
                            onChange={(e) => setNewTagName(e.target.value)}
                            placeholder="Enter tag name..."
                            className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400 text-sm"
                            maxLength={50}
                        />
                        <button
                            type="submit"
                            disabled={!newTagName.trim() || isSubmitting}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm transition-colors disabled:opacity-50"
                        >
                            Add
                        </button>
                    </form>
                </div>

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="w-full px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                >
                    Done
                </button>
            </div>
        </div>
    );
}
